import type { CanvasOp, ClientMessage, ServerMessage } from "@refinement-canvas/domain";
import type { RealtimeClient } from "./realtime-client";

interface CreateOptions {
  readonly websocketUrl: string;
  readonly createSocket?: ((url: string) => WebSocket) | undefined;
  /**
   * keepalive ping の送信間隔（ミリ秒）。テストでの上書き用。
   * 既定は API Gateway WebSocket のアイドルタイムアウト（10 分）より十分短い 5 分。
   */
  readonly keepAliveIntervalMs?: number | undefined;
  /** 一時メッセージ送信待ちキューの上限。テストでの上書き用。 */
  readonly maxPendingTransientMessages?: number | undefined;
}

const SOCKET_OPEN = 1;
// 一時メッセージ（viewer.sync / op.transient）の送信待ち上限。古いものから捨ててよい。
// 永続 op（op.apply）はこの上限とは無関係に、切断中でも一切捨てない（別キューで管理する）。
const DEFAULT_MAX_PENDING_TRANSIENT_MESSAGES = 200;
// API Gateway WebSocket は 10 分間メッセージの送受信が無いと接続を切断する。
// その手前で定期的に ping を送り、編集中・閲覧中の接続が無言で切れるのを防ぐ。
const DEFAULT_KEEPALIVE_INTERVAL_MS = 5 * 60 * 1000;

// 永続させる必要があるメッセージ（サーバへ必ず届けたい op）か判定する。
// op.apply は永続 op の送信であり、切断中も絶対に捨ててはいけない。
// viewer.sync / op.transient / bootstrap.request は最新だけ意味がある一時メッセージで、
// 送信待ちが溢れたら古いものから捨ててよい（カーソル等は再接続時に作り直される）。
function isPersistentMessage(message: ClientMessage): boolean {
  return message.type === "op.apply";
}

// WebSocket 経由のリアルタイム通信クライアント。
// PO Board の websocket-realtime.ts を踏襲し、サーバ権威キュー方式に拡張する。
// - 接続: GET /api/canvases/:id/realtime-session でチケット発行 → wss?ticket=... で接続
// - 送信: 永続 op は op.apply、一時 op は op.transient で送信
// - 受信: viewer.* / op.commit / op.transient / op.reject / bootstrap.snapshot / resync.required
// - 接続維持: 接続中は keepalive ping を定期送信し、アイドルタイムアウトでの切断を防ぐ
// - 切断検知: 確立後の予期せぬ切断は onClose で通知し、呼び出し側（フック）が再接続する
export function createWebSocketRealtimeClient(options: CreateOptions): RealtimeClient {
  let socket: WebSocket | null = null;
  const handlers = new Set<(message: ServerMessage) => void>();
  const closeHandlers = new Set<() => void>();
  const reopenHandlers = new Set<() => void>();
  // 送信待ちを永続 op と一時メッセージで完全に分離する。
  // 永続 op（op.apply）は切断中でも絶対に捨てない。一時メッセージ（viewer.sync / op.transient）は
  // 溢れたら古いものから捨ててよい（カーソル等は最新だけ意味があり、再接続時に作り直される）。
  // 永続 op が一時メッセージの洪水で押し出される事故を構造的に防ぐ。
  const pendingOps: ClientMessage[] = [];
  const pendingTransient: ClientMessage[] = [];
  const maxPendingTransientMessages =
    options.maxPendingTransientMessages ?? DEFAULT_MAX_PENDING_TRANSIENT_MESSAGES;
  const keepAliveIntervalMs = options.keepAliveIntervalMs ?? DEFAULT_KEEPALIVE_INTERVAL_MS;
  let keepAliveTimer: ReturnType<typeof setInterval> | null = null;
  // disconnect() による意図的な切断と、予期せぬ切断（タイムアウト等）を区別するためのフラグ。
  let closedByUser = false;
  // 一度でも接続が確立したか。2 回目以降の open（再接続）でのみ onReopen を発火させるために使う。
  let hasConnectedBefore = false;

  const startKeepAlive = (): void => {
    stopKeepAlive();
    keepAliveTimer = setInterval(() => {
      // 開いている間だけ ping を送る。これによりアイドルタイムアウトでの切断を防ぐ。
      if (socket?.readyState === SOCKET_OPEN) {
        socket.send(JSON.stringify({ type: "ping" } satisfies ClientMessage));
      }
    }, keepAliveIntervalMs);
  };

  const stopKeepAlive = (): void => {
    if (keepAliveTimer !== null) {
      clearInterval(keepAliveTimer);
      keepAliveTimer = null;
    }
  };

  const connect = async (canvasId: string, ticket?: string): Promise<void> => {
    // 既存ソケットがあれば（再接続のやり直し等）、onClose を発火させずに静かに閉じてから張り替える。
    // 先に socket を null にすることで、旧ソケットの onclose ガード（socket === activeSocket）を
    // 無効化し、張り替え時に予期せぬ close 通知や二重接続が起きないようにする。
    if (socket) {
      const previous = socket;
      socket = null;
      stopKeepAlive();
      try {
        previous.close();
      } catch {
        // 既に閉じている場合などは無視する。
      }
    }
    closedByUser = false;
    const params = new URLSearchParams({ canvasId });
    if (ticket) params.set("ticket", ticket);
    const url = `${options.websocketUrl}?${params.toString()}`;
    const activeSocket = (options.createSocket ?? ((socketUrl: string) => new WebSocket(socketUrl)))(
      url
    );
    socket = activeSocket;
    // この接続が一度でも open まで到達したか。open 前後で close の扱いを分けるために使う。
    let opened = false;
    return new Promise((resolve, reject) => {
      activeSocket.onopen = () => {
        opened = true;
        const isReconnect = hasConnectedBefore;
        hasConnectedBefore = true;
        startKeepAlive();
        flushPendingMessages();
        // 再接続（2 回目以降の open）では、presence の再登録などを購読者に促す。
        if (isReconnect) reopenHandlers.forEach((h) => h());
        resolve();
      };
      // open 前の error のみ接続失敗として reject する（open 後の error は close 経由で扱う）。
      activeSocket.onerror = (event) => {
        if (!opened) reject(event);
      };
      activeSocket.onclose = () => {
        // keepAliveTimer はクライアントで 1 個共有のため、旧ソケットの遅延 close が
        // 新ソケット用の keepalive を止めないよう、自分が現役ソケットのときだけ停止する。
        if (socket === activeSocket) stopKeepAlive();
        if (!opened) {
          // 接続確立前の切断は接続失敗として扱う。
          reject(new Error("WebSocket 接続が確立前に切断されました。"));
          return;
        }
        // 確立後の予期せぬ切断（アイドルタイムアウト / ネットワーク断 / 接続時間上限など）。
        // disconnect() による意図的な切断でなければ、購読者へ通知して再接続を促す。
        // すでに別ソケットへ張り替えられている場合（再接続後の旧ソケット close）は通知しない。
        if (!closedByUser && socket === activeSocket) {
          closeHandlers.forEach((h) => h());
        }
      };
      activeSocket.onmessage = (event) => {
        // すでに別ソケットへ張り替えられている場合（再接続後の旧ソケットの遅延メッセージ）は破棄する。
        // 旧ソケットの遅れて届く viewer.snapshot / cursor / view 等が、新ソケットで再同期した直後の
        // 状態を上書きするのを防ぐ（onclose の現役ソケット判定と対称）。
        if (socket !== activeSocket) return;
        try {
          const message = JSON.parse(event.data) as ServerMessage;
          handlers.forEach((h) => h(message));
        } catch (e) {
          console.warn("[realtime] invalid message", e);
        }
      };
    });
  };

  const disconnect = (): void => {
    closedByUser = true;
    stopKeepAlive();
    if (socket?.readyState === SOCKET_OPEN) {
      socket.send(JSON.stringify({ type: "viewer.leave" } satisfies ClientMessage));
    }
    pendingOps.length = 0;
    pendingTransient.length = 0;
    socket?.close();
    socket = null;
  };

  const sendMessage = (message: ClientMessage): void => {
    if (socket?.readyState !== SOCKET_OPEN) {
      enqueuePendingMessage(message);
      return;
    }
    socket.send(JSON.stringify(message));
  };

  const sendOp = (op: CanvasOp): void => {
    sendMessage({ type: "op.apply", op });
  };

  const onMessage = (handler: (message: ServerMessage) => void): (() => void) => {
    handlers.add(handler);
    return () => {
      handlers.delete(handler);
    };
  };

  const onClose = (handler: () => void): (() => void) => {
    closeHandlers.add(handler);
    return () => {
      closeHandlers.delete(handler);
    };
  };

  const onReopen = (handler: () => void): (() => void) => {
    reopenHandlers.add(handler);
    return () => {
      reopenHandlers.delete(handler);
    };
  };

  return { connect, disconnect, sendOp, sendMessage, onMessage, onClose, onReopen };

  function enqueuePendingMessage(message: ClientMessage): void {
    if (isPersistentMessage(message)) {
      // 永続 op は一切捨てない。切断が長期化して再接続が上限回数に達した場合は、フック側が
      // エラー状態へ遷移して全画面リロードで復旧するため、このキューが青天井に伸び続けることはない。
      pendingOps.push(message);
      return;
    }
    // 一時メッセージ（CanvasStage マウント直後の初回 viewer.sync・カーソル・view・ペン途中など）。
    // 上限超過時は古いものから捨てる（最新だけ意味があり、再接続時に作り直されるため）。
    pendingTransient.push(message);
    if (pendingTransient.length > maxPendingTransientMessages) {
      pendingTransient.shift();
    }
  }

  function flushPendingMessages(): void {
    // 永続 op を先に、続けて一時メッセージを送る。
    // 1 件ずつ送信し、送信途中で切断された場合は残りをキューに残す（splice で先取りして
    // 途中 close により未送信分を取りこぼすのを防ぐ）。残った分は次の (再)接続で送り直される。
    while (pendingOps.length > 0) {
      if (socket?.readyState !== SOCKET_OPEN) return;
      socket.send(JSON.stringify(pendingOps[0]));
      pendingOps.shift();
    }
    while (pendingTransient.length > 0) {
      if (socket?.readyState !== SOCKET_OPEN) return;
      socket.send(JSON.stringify(pendingTransient[0]));
      pendingTransient.shift();
    }
  }
}
